import { IButtonStyles } from '@fluentui/react/lib/Button';
import { FontWeights, IRawStyle, ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors } from '../teams-colors';
import { ShadowLevels } from '../teams-shadow-levels';
import { teamsBorderRadius, teamsButtonBorderRadius } from './constants';

const buttonMinWidth = '96px';
const iconSize = '32px';

export function getDefaultButtonStyles(
  themeName: string,
  theme: ITheme
): Partial<IButtonStyles> {
  let primaryTextColor = theme.palette.white;
  let primaryBackgroundColor = theme.palette.themePrimary;
  let primaryBackgroundHoverColor = theme.palette.themeDark;
  let primaryBackgroundActiveColor = theme.palette.themeDarker;
  let primaryBackgroundFocusColor = theme.palette.themeDark;
  let primaryDividerColor = theme.palette.neutralTertiaryAlt;
  let focusBoxShadowColor = theme.palette.white;
  let focusBackgroundColor = theme.palette.neutralTertiaryAlt;
  let focusTextColor = theme.palette.black;
  let focusPrimaryTextColor = theme.palette.white;

  if (themeName === 'dark') {
    primaryTextColor = theme.palette.black;
    primaryBackgroundColor = theme.palette.themeTertiary;
    primaryBackgroundHoverColor = theme.palette.themeSecondary;
    primaryBackgroundActiveColor = theme.palette.themeLight;
    primaryBackgroundFocusColor = theme.palette.themeLight;
    primaryDividerColor = theme.palette.white;
    focusTextColor = theme.palette.black;
    focusPrimaryTextColor = theme.palette.black;
  }

  if (themeName === 'contrast') {
    primaryTextColor = theme.palette.white;
    primaryBackgroundColor = theme.palette.black;
    primaryBackgroundHoverColor = theme.palette.themeDark;
    primaryBackgroundActiveColor = theme.palette.themeDark;
    primaryDividerColor = theme.palette.white;
    focusBoxShadowColor = 'transparent';
    focusBackgroundColor = theme.palette.themeDark;
    focusTextColor = theme.palette.white;
  }

  const commonStyles = {
    borderColor: 'transparent',
    background: primaryBackgroundColor,
    color: primaryTextColor,
    boxShadow: ShadowLevels.shadowLevel1Dark,
    selectors: {
      ':hover': {
        borderColor: 'transparent',
        background: primaryBackgroundHoverColor,
        color: primaryTextColor
      },
      ':active': {
        borderColor: 'transparent',
        background: primaryBackgroundActiveColor,
        color: primaryTextColor
      },
      'svg, .icons-default-fill': {
        fill: primaryTextColor
      }
    }
  };

  const focusStyles = {
    '.ms-Fabric--isFocusVisible &': {
      selectors: {
        ':focus::after': {
          outline: 'none',
          borderRadius: teamsButtonBorderRadius,
          borderColor: theme.palette.white
        },
        ':focus': {
          borderColor: theme.palette.black
        }
      }
    }
  };

  const styles = {
    root: {
      borderRadius: teamsButtonBorderRadius,
      backgroundColor: theme.palette.white,
      borderColor: theme.palette.neutralTertiaryAlt,
      color: theme.palette.black,
      boxShadow: ShadowLevels.shadowLevel1,
      cursor: 'pointer',
      minWidth: buttonMinWidth,
      selectors: {
        '.icons-default-fill': {
          fill: theme.palette.black
        },
        '.icons-filled': {
          display: 'none'
        }
      }
    },
    rootHovered: {
      backgroundColor: theme.palette.neutralQuaternary,
      borderColor: theme.palette.neutralTertiary,
      color: theme.palette.black,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.black
        },
        ...focusStyles
      }
    },
    rootPressed: {
      backgroundColor: theme.palette.neutralTertiaryAlt,
      borderColor: theme.palette.neutralTertiary,
      boxShadow: 'none',
      color: theme.palette.black
    },
    rootChecked: {
      backgroundColor: theme.palette.neutralTertiaryAlt,
      borderColor: theme.palette.neutralTertiary,
      color: theme.palette.black,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.black
        },
        ...focusStyles
      }
    },

    rootCheckedHovered: {
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.black
        },
        ...focusStyles
      }
    },
    rootDisabled: {
      backgroundColor: theme.palette.neutralQuaternary,
      color: theme.palette.neutralTertiary,
      borderColor: 'transparent',
      boxShadow: 'none',
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.neutralTertiary
        },
        ...focusStyles
      }
    },
    rootFocused: {
      backgroundColor: theme.palette.white,
      borderColor: theme.palette.neutralTertiary,
      color: theme.palette.black,
      boxShadow: ShadowLevels.shadowLevel1,
      selectors: {
        'svg, .icons-default-fill': {
          fill: theme.palette.black
        },
        ...focusStyles
      }
    },
    icon: {
      height: iconSize,
      selectors: {
        svg: {
          height: iconSize,
          width: iconSize,
          marginRight: '-9px',
          marginLeft: '-9px'
        }
      }
    },
    menuIcon: {
      selectors: {
        svg: {
          height: '28px',
          width: '28px',
          marginRight: '-8px',
          marginLeft: '-8px',
          marginTop: '-9px',
          marginBottom: '-9px'
        }
      }
    },
    splitButtonMenuButton: {
      borderTopRightRadius: teamsButtonBorderRadius,
      borderBottomRightRadius: teamsButtonBorderRadius,
      borderWidth: '1px',
      borderStyle: 'solid',
      background: theme.palette.white,
      borderLeft: 0,
      color: theme.palette.black,
      marginLeft: 0,
      selectors: {
        '.icons-filled': {
          display: 'none'
        },
        ':hover': {
          backgroundColor: theme.palette.neutralLighter,
          color: theme.palette.black
        },
        ':active': {
          backgroundColor: theme.palette.neutralTertiaryAlt,
          color: theme.palette.black
        },
        '.ms-Button-icon': {
          color: 'inherit',
          fontWeight: FontWeights.bold
        }
      }
    },
    splitButtonContainer: {
      selectors: {
        '.ms-Button': {
          borderColor: theme.palette.neutralTertiaryAlt,
          backgroundClip: 'padding-box',
          boxShadow: ShadowLevels.shadowLevel1
        },
        '.ms-Button--default': {
          borderRight: 0,
          borderTopRightRadius: '0px',
          borderBottomRightRadius: '0px'
        },
        '.ms-Button--primary': {
          borderRight: 0,
          borderTopRightRadius: '0px',
          borderBottomRightRadius: '0px',
          boxShadow: ShadowLevels.shadowLevel1Dark,
          ...commonStyles
        },
        '.ms-Button--primary + .ms-Button': { ...commonStyles },
        ".ms-Button--primary ~ span[class*='css-']": {
          top: 0,
          bottom: 0,
          backgroundColor: primaryDividerColor
        },
        ".ms-Button--primary + .ms-Button[aria-expanded='true']": {
          background: primaryBackgroundActiveColor,
          color: primaryTextColor
        },
        ':hover': {
          selectors: {
            '.ms-Button': {
              borderColor: theme.palette.neutralTertiary
            },
            '.ms-Button--primary': {
              borderColor: 'transparent'
            },
            '.ms-Button--primary + .ms-Button': {
              borderColor: 'transparent'
            }
          }
        },
        ':active': {
          selectors: {
            '.ms-Button': {
              borderColor: theme.palette.neutralTertiary,
              boxShadow: 'none'
            },
            '.ms-Button--primary': {
              borderColor: 'transparent'
            },
            '.ms-Button--primary + .ms-Button': {
              borderColor: 'transparent'
            }
          }
        }
      }
    },
    splitButtonContainerFocused: {
      selectors: {
        '.ms-Fabric--isFocusVisible &': {
          selectors: {
            ':focus::after': {
              outline: 'none',
              borderRadius: teamsBorderRadius,
              borderColor: theme.palette.black,
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              boxShadow: `${focusBoxShadowColor} 0 0 0 1px inset`
            },
            ':focus': {
              selectors: {
                '.ms-Button': {
                  backgroundColor: focusBackgroundColor,
                  color: focusTextColor
                },
                ".ms-Button ~ span[class*='css-']": {
                  backgroundColor: theme.palette.white
                },
                '.ms-Button--primary': {
                  backgroundColor: primaryBackgroundFocusColor,
                  color: focusPrimaryTextColor
                },
                '.ms-Button--primary + .ms-Button': {
                  backgroundColor: primaryBackgroundFocusColor,
                  color: focusPrimaryTextColor
                }
              }
            }
          }
        }
      }
    },
    splitButtonMenuButtonExpanded: {
      background: theme.palette.neutralTertiaryAlt,
      color: theme.palette.black,
      borderColor: theme.palette.neutralTertiary
    },
    splitButtonDivider: {
      top: 1,
      bottom: 1,
      width: 1,
      backgroundColor: theme.palette.neutralTertiaryAlt
    },
    description: {
      color: 'inherit'
    }
  } as Partial<IButtonStyles>;

  // Dark theme overrides
  if (themeName === 'dark') {
    // Root
    (styles.root as IRawStyle).boxShadow = ShadowLevels.shadowLevel1Dark;
    (styles.root as IRawStyle).borderColor = theme.palette.neutralTertiary;
    (styles.root as IRawStyle).backgroundColor = theme.palette.neutralLight;
  }
  // Contrast theme overrides
  if (themeName === 'contrast') {
    // Root
    (styles.root as IRawStyle).borderWidth = '2px';
    (styles.root as IRawStyle).borderColor = theme.palette.black;
    // Root Hovered
    (styles.rootHovered as IRawStyle).borderColor = theme.palette.themeDark;
    (styles.rootHovered as IRawStyle).color = theme.palette.white;
    (styles.rootHovered as IRawStyle).backgroundColor = theme.palette.themeDark;
    ((styles.rootHovered as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.white;
    // Root Pressed
    (styles.rootPressed as IRawStyle).borderColor = theme.palette.themeDark;
    (styles.rootPressed as IRawStyle).color = theme.palette.white;
    (styles.rootPressed as IRawStyle).backgroundColor = theme.palette.themeDark;
    // Root Checked
    (styles.rootChecked as IRawStyle).borderColor = theme.palette.black;
    (styles.rootChecked as IRawStyle).color = theme.palette.white;
    ((styles.rootChecked as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.white;

    // Root Disabled
    (styles.rootDisabled as IRawStyle).color = ContrastColors.appGreenContrast;
    (styles.rootDisabled as IRawStyle).borderWidth = '2px';
    (styles.rootDisabled as IRawStyle).borderColor =
      ContrastColors.appGreenContrast;
    (styles.rootDisabled as IRawStyle).backgroundColor = theme.palette.white;
    ((styles.rootDisabled as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = ContrastColors.appGreenContrast;
    // Root Focused
    (styles.rootFocused as IRawStyle).color = theme.palette.white;
    (styles.rootFocused as IRawStyle).borderWidth = '2px';
    (styles.rootFocused as IRawStyle).backgroundColor = theme.palette.themeDark;
    (styles.rootFocused as IRawStyle).borderColor = theme.palette.themeDark;
    ((styles.rootFocused as Required<IRawStyle>).selectors[
      'svg, .icons-default-fill'
    ] as IRawStyle).fill = theme.palette.white;
    (((styles.rootFocused as Required<IRawStyle>).selectors[
      '.ms-Fabric--isFocusVisible &'
    ] as Required<IRawStyle>).selectors[
      ':focus::after'
    ] as IRawStyle).borderColor = theme.palette.themeDark;
    (((styles.rootFocused as Required<IRawStyle>).selectors[
      '.ms-Fabric--isFocusVisible &'
    ] as Required<IRawStyle>).selectors[':focus'] as IRawStyle).borderColor =
      theme.palette.themeDark;

    // Splitmenu Button Overrides
    (styles.splitButtonMenuButton as IRawStyle).color = theme.palette.black;
    (styles.splitButtonMenuButton as IRawStyle).borderWidth = '2px';
    ((styles.splitButtonMenuButton as Required<IRawStyle>).selectors[
      ':hover'
    ] as IRawStyle).color = theme.palette.white;

    ((styles.splitButtonMenuButton as Required<IRawStyle>).selectors[
      ':active'
    ] as IRawStyle).color = theme.palette.white;

    ((styles.splitButtonMenuButton as Required<IRawStyle>).selectors[
      ':hover'
    ] as IRawStyle).backgroundColor = theme.palette.themeDark;

    ((styles.splitButtonMenuButton as Required<IRawStyle>).selectors[
      ':active'
    ] as IRawStyle).backgroundColor = theme.palette.themeDark;

    (((styles.splitButtonContainer as Required<IRawStyle>).selectors[
      ':hover'
    ] as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as IRawStyle).borderColor = theme.palette.black;

    (((styles.splitButtonContainer as Required<IRawStyle>).selectors[
      ':active'
    ] as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as IRawStyle).borderColor = theme.palette.black;

    (styles.splitButtonDivider as IRawStyle).width = '2px';

    (styles.splitButtonMenuButtonExpanded as IRawStyle).background =
      theme.palette.themeDark;
    (styles.splitButtonMenuButtonExpanded as IRawStyle).color =
      theme.palette.white;
    (styles.splitButtonMenuButtonExpanded as IRawStyle).borderColor =
      theme.palette.white;
  }
  return styles;
}
